import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bullets = [
  "The 12-signal AI-native GTM readiness scorecard",
  "Efficiency-leak checklist across sales, marketing & CS",
  "The 3 AI plays worth running first (and 4 to skip)",
  "Board-ready 1-pager template for the next revenue review",
];

export const LeadMagnet = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/lead-magnet`, {
        name: form.name.trim(),
        email: form.email.trim(),
      });
      setDone(true);
      toast.success("Checklist on its way — check your inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="checklist"
      data-testid="lead-magnet-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6">
            <div className="text-xs font-mono-ui uppercase tracking-[0.28em] text-violet-400 mb-4">
              ─── Free Resource
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-light tracking-tighter text-zinc-50 leading-[1.02]">
              The AI-Native GTM
              <br />
              <span className="text-zinc-500">Audit Checklist.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-lg">
              A 4-page working document Chris uses on the first week of every
              engagement. Score your motion, spot the leaks, and choose which
              AI plays to run first — all before the next revenue review.
            </p>
            <ul className="mt-8 space-y-3 max-w-md">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-zinc-300">
                  <span className="mt-2 h-px w-4 bg-violet-400/70 shrink-0" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-6">
            <div className="border border-white/10 bg-zinc-950/70 backdrop-blur-md p-6 md:p-8 relative overflow-hidden">
              {/* Faux document preview corner */}
              <div className="absolute -top-6 -right-6 h-32 w-32 border border-violet-500/20 rotate-6 opacity-40 pointer-events-none" aria-hidden />
              <div className="flex items-center gap-3 pb-5 border-b border-white/5">
                <span className="inline-flex h-9 w-9 items-center justify-center border border-white/10 bg-violet-500/10">
                  <FileText className="h-4 w-4 text-violet-300" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="text-sm text-zinc-100 font-display">
                    catlyz_gtm_audit_v1.pdf
                  </div>
                  <div className="text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500">
                    4 pages · Delivered to your inbox
                  </div>
                </div>
              </div>

              {done ? (
                <div
                  data-testid="lead-magnet-success"
                  className="py-10 text-center"
                >
                  <CheckCircle2 className="h-8 w-8 text-violet-300 mx-auto" strokeWidth={1.5} />
                  <div className="mt-5 font-display text-xl text-zinc-50">
                    Check your inbox.
                  </div>
                  <p className="mt-2 text-sm text-zinc-400 max-w-sm mx-auto leading-relaxed">
                    The checklist is on its way to{" "}
                    <span className="text-zinc-200">{form.email}</span>. If it
                    hasn’t landed in a minute, check promotions or spam.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="lead-magnet-form"
                  className="mt-6 space-y-4"
                  noValidate
                >
                  <div>
                    <label className="block text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500 mb-2">
                      Name <span className="text-violet-400">*</span>
                    </label>
                    <input
                      data-testid="lead-magnet-input-name"
                      value={form.name}
                      onChange={upd("name")}
                      required
                      placeholder="Your name"
                      className="w-full h-11 bg-zinc-950/60 border border-white/10 text-zinc-50 px-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500 mb-2">
                      Work Email <span className="text-violet-400">*</span>
                    </label>
                    <input
                      data-testid="lead-magnet-input-email"
                      type="email"
                      value={form.email}
                      onChange={upd("email")}
                      required
                      placeholder="you@company.com"
                      className="w-full h-11 bg-zinc-950/60 border border-white/10 text-zinc-50 px-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-magnet-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-zinc-50 text-zinc-950 h-11 text-sm font-medium hover:bg-white disabled:opacity-60 transition-all"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4" />
                    )}
                    {submitting ? "Sending" : "Send me the checklist"}
                  </button>
                  <p className="text-[11px] text-zinc-500 leading-relaxed">
                    One email. No newsletter, no drip sequence. You can reply
                    directly to Chris.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
